import { useEffect, useState } from 'react'
import { Alert, AlertIcon, Box, Button, Card, CardBody, CardHeader, Flex, FormControl, FormLabel, HStack, Heading, Input, Spinner, VStack } from '@chakra-ui/react'
import { UiBreadcrumb } from 'components/common'
import { createAdhocQuote, previewAdhocQuote, getAdhocQuoteById, getCustomerById, fetchDataSafely } from 'api'
import { useAppContext } from 'hooks/useAppContext'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useImmer } from 'use-immer'
import { format } from 'date-fns'
import { AdhocCustomerForm } from './AdhocCustomerForm'
import { AdhocAdditionalItemsForm } from './AdhocAdditionalItemsForm'
import { AdhocPaymentTermsForm } from './AdhocPaymentTermsForm'
import { getAssetUrl } from 'lib'

const BreadcrumbNav = [
  {
    label: 'Adhoc Quotes',
    path: '/quotes/adhoc-quotes'
  },
  {
    label: 'Add',
    isCurrentPage: true
  }
]

const BreadcrumbNavDuplicate = [
  {
    label: 'Adhoc Quotes',
    path: '/quotes/adhoc-quotes'
  },
  {
    label: 'Duplicate',
    isCurrentPage: true
  }
]

export const AddAdhocQuote = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const duplicateFromId = searchParams.get('duplicateFrom')
  const { createToast, currentUser } = useAppContext()
  const [isDuplicating, setIsDuplicating] = useState(!!duplicateFromId)
  const [duplicateSourceNo, setDuplicateSourceNo] = useState(null)
  const [form, setForm] = useImmer({
    quoteDate: format(new Date(), 'yyyy-MM-dd'),
    quoteValidTo: format(new Date(new Date().getTime() + (90 * 24 * 60 * 60 * 1000)), 'yyyy-MM-dd'),
    customer: null,
    salesUserId: currentUser?.id,
    paymentTerms: '',
    additionalItems: [],
    totalCost: 0
  })
  const [isCreating, setIsCreating] = useState(false)
  const [isPreviewing, setIsPreviewing] = useState(false)

  const handleChange = (e) => {
    setForm(draft => {
      draft[e.target.name] = e.target.value
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    const data = {
      ...form,
      customerId: form.customer.id,
      additionalItems: JSON.stringify(form.additionalItems)
    }

    const buttonName = e.nativeEvent.submitter.name
    switch (buttonName) {
      case 'btn-create-quote':
        setIsCreating(true)
        try {
          const newQuote = await createAdhocQuote(data)
          createToast('success', 'Adhoc quote created')
          navigate('/quotes/adhoc-quotes', { state: { newQuote } })
          setIsCreating(false)
        } catch (err) {
          createToast('error', 'Error', err.response.data)
          setIsCreating(false)
        }
        break
      case 'btn-preview-quote':
        setIsPreviewing(true)
        try {
          const { pdfPath } = await previewAdhocQuote(data)
          window.open(getAssetUrl(pdfPath))
          setIsPreviewing(false)
        } catch (err) {
          createToast('error', 'Error', err.response.data)
          setIsPreviewing(false)
        }
        break
    }
  }

  useEffect(() => {
    if (!duplicateFromId) return

    const load = async () => {
      setIsDuplicating(true)
      try {
        const quote = await getAdhocQuoteById(duplicateFromId)
        if (!quote) {
          createToast('error', 'Error', 'Could not load the quote to duplicate.')
          setIsDuplicating(false)
          return
        }
        setDuplicateSourceNo(quote.quoteNo)

        const customer = quote.customerId
          ? await fetchDataSafely(() => getCustomerById(quote.customerId))
          : null

        const additionalItems = (quote.additionalItems ? JSON.parse(quote.additionalItems) : [])
          .map((item, index) => ({ ...item, id: index + 1 }))

        setForm(() => ({
          quoteDate: format(new Date(), 'yyyy-MM-dd'),
          quoteValidTo: format(new Date(new Date().getTime() + (90 * 24 * 60 * 60 * 1000)), 'yyyy-MM-dd'),
          customer: customer || null,
          salesUserId: quote.salesUserId || currentUser?.id,
          paymentTerms: quote.paymentTerms || '',
          additionalItems,
          totalCost: quote.totalCost || 0
        }))
      } catch (err) {
        createToast('error', 'Error', err?.response?.data || 'Failed to duplicate the quote.')
      } finally {
        setIsDuplicating(false)
      }
    }
    load()
  }, [duplicateFromId])

  return (
    <>
      <UiBreadcrumb items={duplicateFromId ? BreadcrumbNavDuplicate : BreadcrumbNav} />
      <Box>
        <Flex justifyContent="space-between" alignItems="center" my={8}>
          <Heading size="lg">{duplicateFromId ? 'Duplicate Adhoc Quote' : 'Create Adhoc Quote'}</Heading>
        </Flex>
        {duplicateFromId && duplicateSourceNo && (
          <Alert status="info" mb={4} borderRadius="md">
            <AlertIcon />
            Duplicated from Quote #{duplicateSourceNo}. Review the values, make any changes, then create the new quote.
          </Alert>
        )}
        {isDuplicating && (
          <Flex align="center" gap={3} mb={4}>
            <Spinner size="sm" />
            <Heading size="sm" color="gray.500">Loading quote details…</Heading>
          </Flex>
        )}
        <Card borderRadius={0}>
          <CardHeader bg="gray.500" p={4}>
            <Heading color="white" size="sm">Information</Heading>
          </CardHeader>
          <CardBody p={8} m="0 auto">
            <form onSubmit={handleSubmit}>
              <VStack gap={10}>
                <HStack gap={10} maxW="100%" width="600px" alignSelf="flex-start">
                  <FormControl isRequired>
                    <FormLabel>Date</FormLabel>
                    <Input type="date" name="quoteDate" value={form.quoteDate} onChange={handleChange} />
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel>Valid To</FormLabel>
                    <Input type="date" name="quoteValidTo" value={form.quoteValidTo} onChange={handleChange} />
                  </FormControl>
                </HStack>
                <AdhocCustomerForm
                  form={form}
                  setForm={setForm}
                />
                <AdhocPaymentTermsForm
                  form={form}
                  setForm={setForm}
                />
                <AdhocAdditionalItemsForm
                  form={form}
                  setForm={setForm}
                />
                <HStack gap={2}>
                  <Button type="submit" name="btn-create-quote" colorScheme="blue" isLoading={isCreating}>Create Quote</Button>
                  <Button type="submit" name="btn-preview-quote" colorScheme="gray" isLoading={isPreviewing}>Preview Quote</Button>
                </HStack>
              </VStack>
            </form>
          </CardBody>
        </Card>
      </Box>
    </>
  )
}
